import { getMartData as fetchMartData } from "api/ApiCalls"
import { applyFetcherOptions } from "helpers/martHelpers"
import type {
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
  SortModelItem,
} from "@tokenterminal/ui/DataTable"
import type { MartFetcherOptions, MartId } from "types/ApiTypes"

export type MartData = Record<string, string | number>

type DataGridSortModelItem = SortModelItem & {
  isSortCaseInsensitive?: boolean
}

const DEFAULT_SSRM_REQUEST: IServerSideGetRowsRequest = {
  startRow: 0,
  endRow: 100,
  rowGroupCols: [],
  valueCols: [],
  pivotCols: [],
  pivotMode: false,
  groupKeys: [],
  filterModel: {},
  sortModel: [],
}

export async function getMartData<T extends MartData = MartData>(
  martId: MartId,
  body: IServerSideGetRowsRequest = DEFAULT_SSRM_REQUEST, // Default body intended for full exporter
  opts?: MartFetcherOptions
): Promise<Array<T>> {
  if (opts) {
    applyFetcherOptions(body, opts)
  }
  const { data } = await fetchMartData<Array<T>>(martId, body)

  return data
}

export async function getAllAvailableValuesFromField<T extends keyof MartData>(
  martId: MartId,
  id: T,
  displayName: string,
  field: T = id
) {
  const data = await getMartData(martId, {
    ...DEFAULT_SSRM_REQUEST,
    endRow: 3000,
    rowGroupCols: [
      {
        id,
        displayName,
        field,
      },
    ],
  })

  return data.map((row) => row[field])
}

function addCaseSentiveOptionsToRequest(params: IServerSideGetRowsParams) {
  const newRequest = { ...params.request }

  newRequest.sortModel.forEach((model) => {
    ;(model as DataGridSortModelItem).isSortCaseInsensitive = !!params.columnApi
      .getColumn(model.colId)
      // todo overwrite existing types
      // @ts-ignore - this is not default type but it does exists
      ?.getColDef().isSortCaseInsensitive
  })

  return newRequest
}

export function createDatasource<T extends MartData = MartData>(
  martId: MartId,
  initialData: Array<T>
): IServerSideDatasource<T> {
  let isInitialFetch = true
  return {
    async getRows(params) {
      try {
        let data: Array<T> = []
        if (isInitialFetch && initialData.length) {
          data = initialData
          isInitialFetch = false
        } else {
          // add case-senstive
          const newRequest = addCaseSentiveOptionsToRequest(params)

          data = await getMartData(martId, newRequest)
        }

        params.success({ rowData: data })
      } catch (err) {
        if (params.api.getModel()) {
          console.error(err)
          params.fail()
        }
      }
    },
  }
}
